import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForgotPassword } from '../../lib/api';
import { AuthLayout } from './auth-layout';
import { Button, Field, FormError, TextInput } from '../../components/ui';

const schema = z.object({ email: z.email('Enter a valid email address.') });
type ForgotForm = z.infer<typeof schema>;

export function ForgotPasswordRoute() {
  const forgot = useForgotPassword();
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit((values) => {
    setFormError(null);
    forgot.mutate(values, {
      onSuccess: () => setSubmitted(true),
      onError: () => setFormError('Could not send the reset link. Please try again.'),
    });
  });

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        footer={
          <Link to="/login" className="link">
            Back to sign in
          </Link>
        }
      >
        {/* Identical whether or not the address is registered. */}
        <p className="text-sm text-neutral-700" data-testid="forgot-confirmation">
          If that address has an account, a password reset link is on its way.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="We will email you a link to choose a new one."
      footer={
        <Link to="/login" className="link">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={formError} />
        <Field label="Email" htmlFor="email" error={errors.email?.message}>
          <TextInput
            id="email"
            type="email"
            autoComplete="email"
            invalid={errors.email !== undefined}
            {...register('email')}
          />
        </Field>
        <Button type="submit" disabled={isSubmitting || forgot.isPending}>
          {forgot.isPending ? 'Sending...' : 'Send reset link'}
        </Button>
      </form>
    </AuthLayout>
  );
}
